import type { CropRect, Preset, Scenario } from "./types";

export const BAPTISM_MASTER_URL = "/scenes/2k/baptism.master.jpg";
export const BAPTISM_MASTER_W = 2048;
export const BAPTISM_MASTER_H = 1143;

// Stage geometry (per multiview-visual-contract.md): the lectern is removed;
// a rectangular baptismal pool is centered on the stage with the officiant and
// the baptismal candidate standing inside, both faces visible. A short queue of
// adult candidates waits on audience-right. Audience-left is empty stage.
// CO-160 §4 names the canonical wide as "Baptism Pool Wide". Crops below are
// normalized (0..1) over the 2048x1143 2K master generated from the original
// 5504x3072 Gemini 3 Pro Image master (2026-05-09).
export const BAPTISM_PRESETS = {
  // CO-160 §4 Baptism Pool Wide — wide pool with all 3 officiant-candidate pairs.
  poolWide: { x: 0.15, y: 0.25, width: 0.72, height: 0.65 },
  // Tighter framing on the center officiant-candidate pair.
  centerPair: { x: 0.36, y: 0.28, width: 0.28, height: 0.55 },
  // Mid-shot of the rightmost officiant-candidate pair.
  rightPair: { x: 0.58, y: 0.28, width: 0.28, height: 0.55 },
  // Mid-shot of the stage attendants in suits.
  attendantsMid: { x: 0.82, y: 0.28, width: 0.16, height: 0.60 },
  // Full-stage establishing crop showing pool, queue, and empty audience-left.
  stageWide: { x: 0.02, y: 0.04, width: 0.96, height: 0.88 },
  // Stage Wide favoring audience-left + pool (drops the queue).
  poolStageWide: { x: 0.02, y: 0.10, width: 0.70, height: 0.78 },
} as const satisfies Record<string, CropRect>;

// Per-cam preset library for the baptism scenario. Display order is the array
// order; AW-RP50 numeric buttons (1..10) bind to the first 10.
// CAM 1 (audience-left of array) → favors stage-right framings (waiting queue).
// CAM 2 (centered) → centered pool (officiant + candidate).
// CAM 3 (audience-right of array) → audience-left / wide establishing.
const cam1Presets: Preset[] = [
  { id: "right-pair", label: "Right Pair", cam: "cam1", scenarioId: "baptism", crop: BAPTISM_PRESETS.rightPair },
  { id: "attendants-mid", label: "Attendants Mid", cam: "cam1", scenarioId: "baptism", crop: BAPTISM_PRESETS.attendantsMid },
];

const cam2Presets: Preset[] = [
  { id: "pool-wide", label: "Pool Wide", cam: "cam2", scenarioId: "baptism", crop: BAPTISM_PRESETS.poolWide },
  { id: "center-pair", label: "Center Pair", cam: "cam2", scenarioId: "baptism", crop: BAPTISM_PRESETS.centerPair },
];

const cam3Presets: Preset[] = [
  { id: "stage-wide", label: "Stage Wide", cam: "cam3", scenarioId: "baptism", crop: BAPTISM_PRESETS.stageWide },
  { id: "pool-stage-wide", label: "Pool Stage Wide", cam: "cam3", scenarioId: "baptism", crop: BAPTISM_PRESETS.poolStageWide },
];

export const BAPTISM: Scenario = {
  id: "baptism",
  label: "Baptism",
  master: {
    url: BAPTISM_MASTER_URL,
    width: BAPTISM_MASTER_W,
    height: BAPTISM_MASTER_H,
  },
  presets: { cam1: cam1Presets, cam2: cam2Presets, cam3: cam3Presets },
  defaults: {
    cam1: "right-pair",
    cam2: "pool-wide",
    cam3: "stage-wide",
  },
};
